import { waitForReady } from './heap'
import type { IdentifyProps, TrackProps } from './types'

function getGoogleId(): string | undefined {
  if (typeof document === 'undefined') return undefined

  try {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === '_ga' && value) {
        // _ga cookie format: GA1.2.XXXXXXXXXX.XXXXXXXXXX
        const parts = value.split('.')
        if (parts.length >= 4) {
          return parts.slice(2).join('.')
        }
      }
    }
  } catch {
    // Ignore cookie parsing errors
  }
  return undefined
}

/**
 * Identify a user. Waits for Heap to be ready before calling.
 * Safe to call before initHeap() - will wait up to 10s for initialization.
 */
export async function identify({ knockAccountId, email, browserId, googleId }: IdentifyProps = {}): Promise<void> {
  if (typeof window === 'undefined') return

  const isReady = await waitForReady()
  if (!isReady) return

  const heap = typeof window !== 'undefined' ? window.heap : undefined
  if (!heap) return

  if (knockAccountId) {
    try {
      heap.identify(String(knockAccountId))
    } catch (err) {
      console.error('[analytics] identify error:', err)
    }
  }

  const payload: Record<string, string> = {}

  if (browserId) {
    payload.browser_id = String(browserId)
  }

  const gid = googleId || getGoogleId()
  if (gid) {
    payload.googleId = String(gid)
  }

  if (email) {
    payload.email = email
  }

  try {
    if (Object.keys(payload).length) {
      heap.addUserProperties(payload)
    }
  } catch (err) {
    console.error('[analytics] addUserProperties error:', err)
  }
}

/**
 * Reset user identity. Does not wait for Heap - if not ready, this is a no-op.
 */
export function resetIdentity(): void {
  try {
    if (typeof window !== 'undefined') {
      window.heap?.resetIdentity()
    }
  } catch (err) {
    console.error('[analytics] resetIdentity error:', err)
  }
}

/**
 * Track an event. Does not wait for Heap - events are queued by stub methods.
 */
export function track({
  event,
  event_heap,
  category,
  action,
  action_label,
  action_value,
  attributes,
}: TrackProps): void {
  if (!event || !category || !action || !action_label) {
    console.error('[analytics] track error: event, category, action and action_label are required.')
    return
  }

  // Non-interaction events for GTM
  if (action === 'non_interaction') {
    attributes = attributes ?? {}
    attributes.non_interaction = attributes.non_interaction ?? true
  }

  // Build Heap payload
  const heapPayload = {
    category,
    action,
    action_label,
    action_value,
    ...attributes,
  }

  // Heap track - stub methods queue calls, so this works before SDK is ready
  try {
    if (typeof window !== 'undefined') {
      window.heap?.track(event_heap || event, heapPayload)
    }
  } catch (err) {
    console.error('[analytics] track error (heap):', err)
  }

  // GTM dataLayer push
  try {
    if (typeof window !== 'undefined' && (window as unknown as { dataLayer?: unknown[] }).dataLayer) {
      ;(window as unknown as { dataLayer: unknown[] }).dataLayer.push({
        event: event,
        ...heapPayload,
        non_interaction: attributes?.non_interaction,
      })
    }
  } catch (err) {
    console.debug('[analytics] track error (gtm):', err)
  }
}
